import type { TeamMemberRow } from "@/hooks/queries";

/**
 * Field-level data-completeness logic for a team member.
 *
 * Why a separate module: completeness is read in three places that
 * shouldn't drift apart, the per-card badge on /team, the "X members
 * need attention" widget at the top of /team, and the admin
 * companies-list summary. Centralising the rules also makes them
 * tweakable in one place when the product spec changes (e.g. once
 * we start collecting allergies separately from dietary, that
 * "missing" check goes here).
 *
 * Two levels:
 *
 *   - REQUIRED: at least one celebration date (birthday OR start_date).
 *     Without either, the member generates no celebrations and an
 *     order can't be placed for them. They show as a hard "Missing
 *     info" coral badge.
 *
 *   - RECOMMENDED: dietary_requirements / allergies present, OR the
 *     buyer has explicitly confirmed "no dietary needs / no allergies"
 *     via the dietary_confirmed / allergies_confirmed flags (MS390).
 *     An empty array alone is ambiguous (unknown vs explicitly none)
 *     and used to flag every member of every team forever. Shown as
 *     a softer butter badge when flagged.
 *
 * Pre-seeded rows (data_source='admin_seeded') get a third treatment:
 * even if the row passes the strict checks, if it was admin-seeded
 * the user gets a one-time "Confirm details" prompt so they can
 * verify the data we filled in for them.
 */

export interface MissingFieldsReport {
  /** Hard-missing fields that block normal celebration flow. */
  required: Array<"birthday" | "start_date">;
  /** Soft-missing fields the user should confirm. */
  recommended: Array<"dietary_requirements" | "allergies">;
  /** True if the row was admin-pre-seeded and the user hasn't enriched it yet. */
  needsAdminSeedConfirmation: boolean;
  /** Convenience: any kind of issue surfaces this. Used by widget counts. */
  hasIssues: boolean;
  /** Convenience: only required-level issues. Used to gate order creation. */
  hasBlockingIssues: boolean;
}

type CompletenessSubset = Pick<
  TeamMemberRow,
  | "birthday"
  | "start_date"
  | "dietary_requirements"
  | "allergies"
  | "data_source"
  | "enriched_at"
> & {
  // MS390 Bug #8: optional so older callers / fixtures without
  // these flags continue to compile. Default false = "buyer hasn't
  // confirmed yet" (the legacy behaviour).
  allergies_confirmed?: boolean;
  dietary_confirmed?: boolean;
};

export function getMissingFields(member: CompletenessSubset): MissingFieldsReport {
  const required: MissingFieldsReport["required"] = [];
  const recommended: MissingFieldsReport["recommended"] = [];

  // Without at least one date the member can't be celebrated. We don't
  // require BOTH because a contractor or an executive who declines to
  // share their birthday is a legitimate case, a work anniversary is
  // still actionable.
  if (!member.birthday && !member.start_date) {
    required.push("birthday");
    required.push("start_date");
  } else {
    if (!member.birthday) required.push("birthday");
    if (!member.start_date) required.push("start_date");
  }

  // MS390 Bug #8: only flag dietary as "needs attention" when the
  // array is empty AND the buyer hasn't explicitly confirmed it.
  // Pre-flag behaviour treated every empty-array row as "missing
  // info" which made the /team banner fire forever — even when the
  // member legitimately has no dietary preferences. The buyer ticks
  // "No dietary requirements" on the form to set dietary_confirmed.
  const dietaryEmpty =
    !member.dietary_requirements || member.dietary_requirements.length === 0;
  if (dietaryEmpty && !member.dietary_confirmed) {
    recommended.push("dietary_requirements");
  }

  // Allergies follow the same pattern: empty + unconfirmed = flag,
  // empty + confirmed = legitimately "no allergies", don't flag.
  // This is the load-bearing fix for the "Team needs attention"
  // banner firing for the entire team forever.
  const allergiesEmpty = !member.allergies || member.allergies.length === 0;
  if (allergiesEmpty && !member.allergies_confirmed) {
    recommended.push("allergies");
  }

  // A pre-seeded row that the user hasn't touched still warrants a
  // "please confirm" treatment even if every field happens to be set
  // (rare, but possible if the admin seeded a fully-populated CSV).
  // The CSV import + manual edits both stamp enriched_at, so absence
  // of that timestamp is the marker of an unconfirmed row.
  const needsAdminSeedConfirmation =
    member.data_source === "admin_seeded" && !member.enriched_at;

  return {
    required,
    recommended,
    needsAdminSeedConfirmation,
    hasIssues:
      required.length > 0 || recommended.length > 0 || needsAdminSeedConfirmation,
    hasBlockingIssues: required.length > 0,
  };
}

/**
 * Human-readable label for a missing field. Used in chips on team cards
 * and in the "X members need attention" widget's hover-card detail.
 */
export function fieldLabel(
  field:
    | MissingFieldsReport["required"][number]
    | MissingFieldsReport["recommended"][number],
): string {
  switch (field) {
    case "birthday":
      return "Birthday";
    case "start_date":
      return "Start date";
    case "dietary_requirements":
      return "Dietary";
    case "allergies":
      return "Allergies";
    default:
      return field;
  }
}

/**
 * Aggregate the report across a list, used for the team-page widget.
 *
 * The buckets are MUTUALLY EXCLUSIVE, each member is counted in at
 * most one bucket so the widget's display math (sum the buckets)
 * never produces a negative or double-counted total. Bucket priority
 * mirrors the visual urgency we want to convey:
 *   1. blocking, missing celebration date, can't generate orders
 *   2. pendingConfirmation, pre-seeded by admin, needs user review
 *   3. recommendedOnly, only soft fields missing (dietary, allergies)
 *
 * `withIssues` is the sum of the three buckets (== total members
 * with any issue at all). Older field names (withBlockingIssues,
 * unconfirmedSeeded) are kept as aliases for callers we haven't
 * migrated yet, but new code should use the explicit bucket names.
 */
export interface TeamCompletenessSummary {
  total: number;
  withIssues: number;
  /** Members whose primary issue is a missing celebration date. */
  blocking: number;
  /** Members whose primary issue is "admin pre-seeded, please confirm" (and no blocking date issue). */
  pendingConfirmation: number;
  /** Members with only soft issues (dietary or allergies missing) and no blocking/seed issues. */
  recommendedOnly: number;
  /** @deprecated Use `blocking`. */
  withBlockingIssues: number;
  /** @deprecated Use `pendingConfirmation`. */
  unconfirmedSeeded: number;
}

export function summariseCompleteness(
  members: CompletenessSubset[],
): TeamCompletenessSummary {
  let blocking = 0;
  let pendingConfirmation = 0;
  let recommendedOnly = 0;

  for (const m of members) {
    const r = getMissingFields(m);
    // Mutually exclusive buckets, highest urgency first. A member
    // missing both a date AND with admin-seed-pending lands in
    // `blocking` only, the pending-confirmation message is implied
    // by the deeper missing-data treatment.
    if (r.hasBlockingIssues) {
      blocking++;
    } else if (r.needsAdminSeedConfirmation) {
      pendingConfirmation++;
    } else if (r.recommended.length > 0) {
      recommendedOnly++;
    }
  }

  const withIssues = blocking + pendingConfirmation + recommendedOnly;

  return {
    total: members.length,
    withIssues,
    blocking,
    pendingConfirmation,
    recommendedOnly,
    // Compat aliases, keep the old names pointing at the new bucket
    // semantics so existing callers don't break. The new bucket math
    // is non-overlapping so the widget formulas can simplify.
    withBlockingIssues: blocking,
    unconfirmedSeeded: pendingConfirmation,
  };
}
